import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Checkbox, Card, Accordion, RadioGroup } from '@core/components/ui';

/**
 * LocationAvailabilityForm Component
 *
 * Complete availability form with geographic, temporal, and logistics sections:
 * - 23 geographic location checkboxes (5 zones)
 * - 5 temporal availability checkboxes
 * - Driver's license (yes/no)
 * - Own vehicle (yes/no, conditional on license)
 *
 * @param {Object} props
 * @param {Object} props.availability - Object with all availability fields
 * @param {Function} props.onChange - Callback when availability changes (receives updated availability object)
 * @param {boolean} props.disabled - Whether form is disabled
 */
export function LocationAvailabilityForm({ availability, onChange, disabled = false }) {
  const { t } = useTranslation();

  const handleCheckboxChange = (field, checked) => {
    onChange({
      ...availability,
      [field]: checked,
    });
  };

  const handleRadioChange = (field, value) => {
    const updates = {
      ...availability,
      [field]: value,
    };

    // If setting drivers_license to 'no', also clear vehicle_propi
    if (field === 'carnet_conduir' && value === 'no') {
      updates.vehicle_propi = null;
    }

    onChange(updates);
  };

  const locationZones = [
    {
      zoneKey: 'girona',
      locations: [
        { key: 'loc_girona_juia', label: 'Girona/Juià' },
        { key: 'loc_santa_coloma_farners', label: 'Santa Coloma de Farners' },
        { key: 'loc_sils', label: 'Sils' },
        { key: 'loc_blanes', label: 'Blanes' },
        { key: 'loc_lloret', label: 'Lloret' },
        { key: 'loc_tordera_girona', label: 'Tordera' },
        { key: 'loc_llora', label: 'Llorà' },
      ],
    },
    {
      zoneKey: 'barcelona',
      locations: [
        { key: 'loc_barcelona', label: 'Barcelona' },
        { key: 'loc_rubi', label: 'Rubí' },
        { key: 'loc_santa_maria_palau_tordera', label: 'Santa Maria Palau Tordera' },
        { key: 'loc_badalona', label: 'Badalona' },
        { key: 'loc_sabadell', label: 'Sabadell' },
        { key: 'loc_hospitalet_llobregat', label: "Hospitalet de Llobregat" },
        { key: 'loc_terrassa', label: 'Terrassa' },
        { key: 'loc_tordera_barcelona', label: 'Tordera' },
      ],
    },
    {
      zoneKey: 'tarragona',
      locations: [
        { key: 'loc_reus', label: 'Reus' },
        { key: 'loc_valls', label: 'Valls' },
      ],
    },
    {
      zoneKey: 'lleida',
      locations: [{ key: 'loc_lleida', label: 'Lleida' }],
    },
    {
      zoneKey: 'terresEbre',
      locations: [
        { key: 'loc_tortosa', label: 'Tortosa' },
        { key: 'loc_gandesa', label: 'Gandesa' },
        { key: 'loc_roquetes', label: 'Roquetes' },
        { key: 'loc_deltebre', label: 'Deltebre' },
        { key: 'loc_falset', label: 'Falset' },
      ],
    },
  ];

  // Temporal availability options
  const temporalOptions = [
    { key: 'disponibilitat_matins', labelKey: 'matins' },
    { key: 'disponibilitat_tardes', labelKey: 'tardes' },
    { key: 'disponibilitat_nits', labelKey: 'nits' },
    { key: 'disponibilitat_festius', labelKey: 'festius' },
    { key: 'disponibilitat_caps_setmana', labelKey: 'capsSetmana' },
  ];

  // Radio options for yes/no questions
  const yesNoOptions = [
    { value: 'si', label: t('common.yes') },
    { value: 'no', label: t('common.no') },
  ];

  return (
    <Card title={t('locationAvailability.formTitle')}>
      <div className="space-y-6">
        {/* Section 1: Geographic Availability - Only zones in accordions */}
        <div>
          <h3 className="text-base font-semibold text-default mb-3">
            {t('locationAvailability.sections.geographic')}
          </h3>
          <div className="space-y-2">
            {locationZones.map(({ zoneKey, locations }) => (
              <Accordion
                key={zoneKey}
                header={
                  <h4 className="text-sm font-medium text-muted">
                    {t(`locationAvailability.zones.${zoneKey}`)}
                  </h4>
                }
              >
                <div className="space-y-2">
                  {locations.map(({ key, label }) => (
                    <Checkbox
                      key={key}
                      name={key}
                      label={label}
                      checked={availability[key] || false}
                      onChange={(checked) => handleCheckboxChange(key, checked)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              </Accordion>
            ))}
          </div>
        </div>

        {/* Section 2: Temporal Availability - Fully expanded */}
        <div>
          <h3 className="text-base font-semibold text-default mb-3">
            {t('locationAvailability.sections.temporal')}
          </h3>
          <div className="space-y-2">
            {temporalOptions.map(({ key, labelKey }) => (
              <Checkbox
                key={key}
                name={key}
                label={t(`locationAvailability.temporal.${labelKey}`)}
                checked={availability[key] || false}
                onChange={(checked) => handleCheckboxChange(key, checked)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>

        {/* Section 3: Driver's License & Vehicle - Fully expanded */}
        <div>
          <h3 className="text-base font-semibold text-default mb-3">
            {t('locationAvailability.sections.driversLicense')}
          </h3>
          <div className="space-y-4">
            <RadioGroup
              name="carnet_conduir"
              options={yesNoOptions}
              value={availability.carnet_conduir}
              onChange={(value) => handleRadioChange('carnet_conduir', value)}
              disabled={disabled}
            />

            {/* Own Vehicle (conditional on license) */}
            {availability.carnet_conduir === 'si' && (
              <div className="pt-2 border-t border-border-default">
                <h4 className="text-sm font-medium text-muted mb-2">
                  {t('locationAvailability.sections.ownVehicle')}
                </h4>
                <RadioGroup
                  name="vehicle_propi"
                  options={yesNoOptions}
                  value={availability.vehicle_propi}
                  onChange={(value) => handleRadioChange('vehicle_propi', value)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

LocationAvailabilityForm.propTypes = {
  availability: PropTypes.shape({
    // ZONA GIRONA
    loc_girona_juia: PropTypes.bool,
    loc_santa_coloma_farners: PropTypes.bool,
    loc_sils: PropTypes.bool,
    loc_blanes: PropTypes.bool,
    loc_lloret: PropTypes.bool,
    loc_tordera_girona: PropTypes.bool,
    loc_llora: PropTypes.bool,
    // ZONA BARCELONA
    loc_barcelona: PropTypes.bool,
    loc_rubi: PropTypes.bool,
    loc_santa_maria_palau_tordera: PropTypes.bool,
    loc_badalona: PropTypes.bool,
    loc_sabadell: PropTypes.bool,
    loc_hospitalet_llobregat: PropTypes.bool,
    loc_terrassa: PropTypes.bool,
    loc_tordera_barcelona: PropTypes.bool,
    // ZONA TARRAGONA
    loc_reus: PropTypes.bool,
    loc_valls: PropTypes.bool,
    // ZONA LLEIDA
    loc_lleida: PropTypes.bool,
    // ZONA TERRES DE L'EBRE
    loc_tortosa: PropTypes.bool,
    loc_gandesa: PropTypes.bool,
    loc_roquetes: PropTypes.bool,
    loc_deltebre: PropTypes.bool,
    loc_falset: PropTypes.bool,
    // TEMPORAL AVAILABILITY
    disponibilitat_matins: PropTypes.bool,
    disponibilitat_tardes: PropTypes.bool,
    disponibilitat_nits: PropTypes.bool,
    disponibilitat_festius: PropTypes.bool,
    disponibilitat_caps_setmana: PropTypes.bool,
    // LOGISTICS
    carnet_conduir: PropTypes.oneOf(['si', 'no', null]),
    vehicle_propi: PropTypes.oneOf(['si', 'no', null]),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
